import type { EnumUnion } from "@dresscode-frontend/utility-types";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError, handleApiError } from "@/lib/api/error";
import { remainingTimeToday } from "@/lib/query";
import { queryClient } from "@/lib/queryClient";
import { genNameInitials } from "@/utils/name/initials";

import type { GroupIdMemberTableRow } from "./-types";
import { convertProxyUserItemToTableRow } from "./_layout/_list/-table/type";

const convertToGroupIdMembersTableRow = (
  data: GetGroupIdMembersRes,
): GroupIdMemberTableRow[] => {
  return data.items.map((item) => ({
    person: {
      id: item.personId,
      name: item.name,
      nameInitials: genNameInitials(item.name),
      email: item.email || "",
      avatarUrl: item.avatarUrl || "",
    },
  }));
};

/**
 * Keys for query
 */
const PROXY_USER_API_KEYS = {
  all: ["proxy-users"] as const,
  list: (params: GetProxyUserListQuery) =>
    [...PROXY_USER_API_KEYS.all, "list", params] as const,
  count: () => [...PROXY_USER_API_KEYS.all, "count"] as const,
  groupIdAll: (proxyUserId: string) => ["group-id", proxyUserId] as const,
  groupIdDetail: (proxyUserId: string) =>
    [...PROXY_USER_API_KEYS.groupIdAll(proxyUserId), "detail"] as const,
  groupIdMembers: (proxyUserId: string) =>
    [...PROXY_USER_API_KEYS.groupIdAll(proxyUserId), "members"] as const,
  groupIdMembersSearch: (
    proxyUserId: string,
    params?: GetGroupIdMembersParams,
  ) =>
    [
      ...PROXY_USER_API_KEYS.groupIdMembers(proxyUserId),
      "search",
      { params },
    ] as const,
} as const;

/**
 * GET /v2/id-management/v2/ids
 */
export type GetProxyUserListQuery = ApiSearchQuery<
  "/v2/id-management/v2/ids",
  "get"
>;
export const ProxyUserOrderBy = {
  PERSON: "PERSON",
  LOGIN_ID: "LOGIN_ID",
  PERSON_MATCH_TYPE: "PERSON_MATCH_TYPE",
  WORKER_TYPE: "WORKER_TYPE",
  WORKER_CONTRACT_TYPE: "WORKER_CONTRACT_TYPE",
  EMPLOYMENT_TYPE: "EMPLOYMENT_TYPE",
  BOARD_MEMBER_TYPE: "BOARD_MEMBER_TYPE",
  CONTRACTOR_TYPE: "CONTRACTOR_TYPE",
  COLLABORATOR_TYPE: "COLLABORATOR_TYPE",
} as const satisfies Record<
  NonNullable<GetProxyUserListQuery["orderBy"]>,
  string
>;
export type ProxyUserOrderBy = EnumUnion<typeof ProxyUserOrderBy>;
export type { GetProxyUserListItem } from "./-types";
export const useGetProxyUserList = (query: GetProxyUserListQuery) => {
  return useQuery({
    queryKey: PROXY_USER_API_KEYS.list(query),
    queryFn: async () => {
      const res = await apiClient.GET("/v2/id-management/v2/ids", {
        params: {
          query: {
            ...query,
            category: query.category,
          },
        },
      });
      return getDataOrThrowApiError(res);
    },
    select: (data) => ({
      items: data.items.map(convertProxyUserItemToTableRow),
      maxPage: data.maxPage,
      count: data.count,
    }),
  });
};

/**
 * GET /v2/id-management/v2/ids-count
 */
export const useGetProxyUserCount = () => {
  return useQuery({
    queryKey: PROXY_USER_API_KEYS.count(),
    queryFn: async () => {
      const res = await apiClient.GET("/v2/id-management/v2/ids-count");
      return getDataOrThrowApiError(res);
    },
  });
};

/**
 * POST /v2/id-management/group-ids
 */
export type CreateGroupIdRequest = ApiRequestBody<
  "/v2/id-management/group-ids",
  "post"
>;
export const usePostGroupId = () => {
  return useMutation({
    mutationFn: async (body: CreateGroupIdRequest) => {
      const res = await apiClient.POST("/v2/id-management/group-ids", {
        body,
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROXY_USER_API_KEYS.all });
    },
  });
};

/**
 * Hook to fetch GroupId details
 */
export type GetGroupIdRes = ApiResponseBody<
  "/v2/id-management/group-ids/{proxyUserId}",
  "get"
>;
export const useGetGroupId = (proxyUserId: string) => {
  return useSuspenseQuery({
    queryKey: PROXY_USER_API_KEYS.groupIdDetail(proxyUserId),
    staleTime: remainingTimeToday(),
    queryFn: async (): Promise<GetGroupIdRes> => {
      const res = await apiClient.GET(
        "/v2/id-management/group-ids/{proxyUserId}",
        {
          params: { path: { proxyUserId } },
        },
      );
      return getDataOrThrowApiError(res);
    },
  });
};

/**
 * Helper function to invalidate GroupId queries
 */
export const invalidateGroupId = (proxyUserId: string) => {
  return queryClient.invalidateQueries({
    queryKey: PROXY_USER_API_KEYS.groupIdAll(proxyUserId),
  });
};

/**
 * Hook to update a GroupId
 */
export type UpdateGroupIdReq = ApiRequestBody<
  "/v2/id-management/group-ids/{proxyUserId}",
  "patch"
>;
export const useUpdateGroupId = (proxyUserId: string) => {
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: async (data: UpdateGroupIdReq): Promise<GetGroupIdRes> => {
      const res = await apiClient.PATCH(
        "/v2/id-management/group-ids/{proxyUserId}",
        {
          params: { path: { proxyUserId } },
          body: data,
        },
      );
      return getDataOrThrowApiError(res);
    },
    onError: (error) => {
      // TODO: エラーハンドリングを行う
      // oxlint-disable-next-line no-unused-expressions
      handleApiError(error, { language: i18n.language }).message;
    },
  });
};

/**
 * Hook to delete a GroupId
 */
export type DeleteGroupIdRes = ApiResponseBody<
  "/v2/id-management/group-ids/{proxyUserId}",
  "delete"
>;
export const useDeleteGroupId = (
  proxyUserId: string,
  options?: {
    onSuccess?: (data: DeleteGroupIdRes) => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  return useMutation({
    mutationFn: async (): Promise<DeleteGroupIdRes> => {
      const res = await apiClient.DELETE(
        "/v2/id-management/group-ids/{proxyUserId}",
        { params: { path: { proxyUserId } } },
      );
      return getDataOrThrowApiError(res);
    },
    onSuccess: (data) => {
      invalidateGroupId(proxyUserId);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const errorMessage = handleApiError(error, {
        language: i18n.language,
      }).message;
      options?.onError?.(errorMessage);
    },
  });
};

/**
 * Hook to fetch GroupId members with optional filtering and pagination
 */
export type GetGroupIdMembersParams = ApiSearchQuery<
  "/v2/id-management/group-ids/{proxyUserId}/members",
  "get"
>;
export type GetGroupIdMembersRes = ApiResponseBody<
  "/v2/id-management/group-ids/{proxyUserId}/members",
  "get"
>;
export const useGroupIdMembers = (
  proxyUserId: string,
  params?: GetGroupIdMembersParams,
) => {
  return useQuery({
    queryKey: PROXY_USER_API_KEYS.groupIdMembersSearch(proxyUserId, params),
    queryFn: async (): Promise<GetGroupIdMembersRes> => {
      const res = await apiClient.GET(
        "/v2/id-management/group-ids/{proxyUserId}/members",
        {
          params: {
            path: { proxyUserId },
            query: params,
          },
        },
      );
      return getDataOrThrowApiError(res);
    },
    select: (data) => ({
      items: convertToGroupIdMembersTableRow(data),
      total: data.length,
      count: data.count,
      pageIndex: data.page,
      maxPage: data.maxPage,
    }),
  });
};
